import type { GameInput } from "../../ui/input/router.ts";
import type { TouchHandlers, TouchView } from "./actions.ts";
import { mountTouchButtons } from "./buttons-mount.ts";
import { isTouchPage, watchTouchPage } from "./page.ts";
import { mountStick, type MountedStick } from "./stick-mount.ts";

/**
 * What the touch controls ask of the scene, which does not exist until the world loads.
 */
export interface TouchScene {
  readonly touchHandlers: TouchHandlers | null;
  readonly touchView: () => TouchView;
  readonly touchLive: () => boolean;
  readonly tapWorld: (x: number, y: number) => void;
}

/**
 * The mounted touch controls.
 */
export interface MountedTouch {
  /**
   * The stick, which the scene reads for the movement intent.
   */
  readonly stick: MountedStick;
  /**
   * Tells the controls whether the world is revealed: they show only while it is (and the scene says the character is in no
   * transfer), never on the loading screen, the entry screens or a notice.
   *
   * @param shown True from the reveal, false when the world is concealed.
   */
  worldShown(shown: boolean): void;
}

/**
 * Mounts the stick and the action buttons in the overlay (ADR-061's amendment of 2026-10-31), shown on a touch page and
 * hidden elsewhere, and only while the player is in the world.
 *
 * @param overlay The overlay root.
 * @param input The input router, whose owner says whether the game has the keyboard.
 * @param scene Reads the scene, or null before it exists.
 * @returns The stick and the world signal.
 */
export function mountTouchControls(overlay: HTMLElement, input: Pick<GameInput, "owner">, scene: () => TouchScene | null): MountedTouch {
  let shown = false;
  const live = (): boolean => shown && scene()?.touchLive() === true;
  const stick = mountStick(overlay, { owner: () => input.owner, px: () => Number.parseFloat(overlay.style.getPropertyValue("--px")) || 1, tap: (x, y) => scene()?.tapWorld(x, y), live });
  const buttons = mountTouchButtons(overlay, { handlers: () => scene()?.touchHandlers ?? null, view: () => scene()?.touchView() ?? null, owner: () => input.owner, live });
  for (const control of [buttons, stick]) {
    control.show(isTouchPage());
    watchTouchPage((touch) => control.show(touch));
  }
  return {
    stick,
    worldShown(next: boolean): void {
      shown = next;
    },
  };
}
